import { FormattedMessage, useIntl } from 'react-intl';
import {
  Form,
  Button,
  FreeForm,
  FormItem,
  FormPage,
  Select,
  RadioGroup,
  InputNumber,
  Divider,
  Checkbox,
} from 'iglooform';
import { history, useLocation } from 'umi';
import { useRequest } from 'ahooks';
import classnames from 'classnames';
import moment from 'moment';
import DatePickerMonth from '@/components/date-picker-month';
import {
  covert2Million,
  getSession,
  covert2RemoveMillion,
} from '@/utils/common';
import { CreateQuote, QuotationConfirm } from './service';
import useMobile from '@/hooks/useMobile';
import useFormItemReaction from './hooks/useFormItemReaction';
import useVehicleOptions from './hooks/useVehicleOptions';
import useHandleFormData from '@/hooks/useHandleFormData';
import * as SESSION_KEYS from '@/configs/session-storage-keys';

import './index.less';

interface Props {
  nextStep: string;
  productType: string;
  needCreateQuote?: boolean;
  changeActiveStep: any;
  isBuyNowFlow?: boolean;
}

const VehicleInfoForm = (props: Props) => {
  const {
    changeActiveStep,
    nextStep,
    needCreateQuote,
    productType,
    isBuyNowFlow = false,
  } = props;
  const isMobile = useMobile();
  const location = useLocation();
  const planPicked = getSession(SESSION_KEYS.PICKED_PLAN_INFO);
  const initFirstStepInfo = covert2RemoveMillion(
    getSession(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE) as {
      [prop: string]: any;
    },
  );

  const { formatMessage } = useIntl();
  const form = Form.useForm();

  const { create } = useHandleFormData();

  const { run: quotationConfirm } = useRequest(QuotationConfirm, {
    manual: true,
    onSuccess: () => {
      const quoteInfo = JSON.parse(
        sessionStorage.getItem(SESSION_KEYS.QUOTE_INFO) ?? '{}',
      );
      // 保证BuyNow流程时，application页面点back按钮可以返回到这里
      const historyJumpFunc = isBuyNowFlow ? history.push : history.replace;
      historyJumpFunc({
        pathname: `/home/${productType}/application`,
        query: {
          productKey: planPicked?.productKey,
          planKey: planPicked?.planKey,
          quotationId: quoteInfo?.id,
          goBackUrl: location.pathname + '?' + location.search,
          isBuyNowFlow: String(isBuyNowFlow),
        },
      });

      //event tracking: start_application
      window.gtag('event', 'start_application', {
        page: 'direct purchase',
        product_key: planPicked?.productKey,
        plan_key: planPicked?.planKey,
        quote_id: quoteInfo?.id,
        user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
          ?.id,
      });
    },
  });

  const {
    paOptions,
    visibilities,
    continueButtonDisalbed,
    onChangeFormItem,
    numberOfSeats,
    vehicleCreateDateObj,
  } = useFormItemReaction(form, initFirstStepInfo);
  const vehicleOptions = useVehicleOptions();
  const { run: createQuote } = useRequest(CreateQuote, {
    manual: true,
    onSuccess: (res) => {
      sessionStorage.setItem(SESSION_KEYS.QUOTE_INFO, JSON.stringify(res));
      if (nextStep === 'application') {
        quotationConfirm(
          productType,
          planPicked?.productKey,
          planPicked?.planKey,
          res?.id,
        );
      } else {
        changeActiveStep(nextStep);
      }
    },
  });

  return (
    <div className={classnames(['vehicleForm', { 'is-mobile': isMobile }])}>
      <FreeForm
        className="formContainer"
        form={form}
        showSubmitButton={false}
        initialValues={{
          vehicleInfo: initFirstStepInfo,
        }}
      >
        <FormPage name="vehicleInfo">
          <FormItem
            name="vehicle_type"
            label={formatMessage({
              id:
                planPicked?.coverageType === 'cpd'
                  ? 'Vehicle Type'
                  : 'Using Purpose',
            })}
            fullRow={false}
          >
            <Select
              className="vehicleFormSelectVehicle"
              onChange={(value) => onChangeFormItem('vehicle_type', value)}
            >
              {vehicleOptions}
            </Select>
          </FormItem>
          {visibilities.business && (
            <FormItem
              name="business_usage"
              label={formatMessage({
                id: 'Business Usage',
              })}
            >
              <RadioGroup
                options={[
                  {
                    label: formatMessage({ id: 'Yes' }),
                    value: 'Yes',
                  },
                  {
                    label: formatMessage({ id: 'No' }),
                    value: 'No',
                  },
                ]}
                onChange={(event) =>
                  onChangeFormItem('business_usage', event.target.value)
                }
              />
            </FormItem>
          )}
          {visibilities.numberOfSeas && (
            <FormItem
              name="seats_number"
              label={formatMessage({
                id: `Number of Seats(Including Driver's)`,
              })}
              fullRow={false}
            >
              <Select
                onChange={(value) => onChangeFormItem('seats_number', value)}
                options={numberOfSeats.map((e) => ({ label: e, value: e }))}
              />
            </FormItem>
          )}

          {visibilities.physicalDamage && !visibilities.phsicalDamageRequired && (
            <>
              <div className="pdFormDividerWrap">
                <Divider className="pdFormDivider" />
              </div>
              <div className="pdFormSectionLabel">
                {formatMessage({
                  id: 'For Auto Physical Damage',
                })}
              </div>
            </>
          )}

          {visibilities.physicalDamage && (
            <FormItem
              name="vehicle_value"
              label={formatMessage({
                id: 'Vehicle Value (Millions)',
              })}
              fullRow
              showOptional={!visibilities.phsicalDamageRequired}
              rules={[
                {
                  validator: (_, value: number) => {
                    if (value < 5) {
                      return Promise.reject(
                        new Error(
                          formatMessage({
                            id: 'Minimum value is 5 millions',
                          }),
                        ),
                      );
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
              required={visibilities.phsicalDamageRequired}
            >
              <InputNumber
                style={{ width: '48.9%' }}
                prefix={formatMessage({ id: 'VND' })}
                onChange={(event) =>
                  onChangeFormItem('vehicle_value', event?.target?.value)
                }
              />
            </FormItem>
          )}

          {visibilities.physicalDamage && (
            <FormItem
              name="vehicle_origin"
              label={formatMessage({
                id: 'Vehicle Origin',
              })}
              showOptional={!visibilities.phsicalDamageRequired}
              required={visibilities.phsicalDamageRequired}
            >
              <Select
                onChange={(value) => onChangeFormItem('vehicle_origin', value)}
                options={[
                  {
                    label: formatMessage({ id: 'Made in Vietnam' }),
                    value: 'Made in Vietnam',
                  },
                  {
                    label: formatMessage({ id: 'Imported Car' }),
                    value: 'Imported Car',
                  },
                ]}
              />
            </FormItem>
          )}

          {visibilities.physicalDamage && (
            <FormItem
              name={vehicleCreateDateObj.name}
              label={<FormattedMessage {...vehicleCreateDateObj['label']} />}
              showOptional={!visibilities.phsicalDamageRequired}
              required={false}
            >
              {/* 限定选本月以及往前的30年 */}
              <DatePickerMonth
                disabledDate={(current) => {
                  if (
                    planPicked?.planName === 'BSH Car Physical Damage - Option1'
                  ) {
                    return (
                      current &&
                      (current > moment().endOf('day') ||
                        current < moment().add(-30, 'y'))
                    );
                  }
                  return (
                    current &&
                    (current > moment().endOf('day') ||
                      current < moment().add(-10, 'y'))
                  );
                }}
                onChange={(value) =>
                  onChangeFormItem(vehicleCreateDateObj.name, value)
                }
              />
            </FormItem>
          )}

          {visibilities.tonnage && (
            <FormItem
              name="tonnage"
              label={formatMessage({
                id: `Tonnage`,
              })}
              required={false}
              rules={[
                {
                  validator: (_: any, value: string) => {
                    if (!value) {
                      return Promise.reject(
                        new Error(
                          formatMessage({
                            id: 'Tonnage is required',
                          }),
                        ),
                      );
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <InputNumber
                onChange={(event) =>
                  onChangeFormItem('tonnage', event.target.value)
                }
                suffix={formatMessage({ id: 'Tonnes' })}
                min={1}
              />
            </FormItem>
          )}

          {visibilities.personalAccident && (
            <FormItem
              name="pa"
              label={formatMessage({ id: 'Add-ons Benefits' })}
              halfRow={true}
              required={false}
              showOptional={true}
            >
              <Checkbox
                onChange={(e: any) => {
                  onChangeFormItem('pa', e.target.checked);
                }}
              >
                {formatMessage({
                  id: 'Personal Accident Insurance for driver and passengers',
                })}
              </Checkbox>
            </FormItem>
          )}
          {visibilities.personalAccidentOptions && (
            <FormItem
              name="pa_persons_num"
              style={{ marginTop: '-12px' }}
              required={false}
              rules={[
                {
                  validator: (_: any, value: string) => {
                    if (!value) {
                      return Promise.reject(
                        new Error(
                          formatMessage({
                            id: 'Add-ons Benefits is required',
                          }),
                        ),
                      );
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <Select
                options={paOptions}
                onChange={(value) => onChangeFormItem('pa_persons_num', value)}
              />
            </FormItem>
          )}
        </FormPage>
      </FreeForm>

      <Button
        className={'continueButton'}
        type="primary"
        disabled={continueButtonDisalbed}
        onClick={() => {
          form.validateFields().then((value) => {
            const vehicleInfo = covert2Million(value.vehicleInfo);
            sessionStorage.setItem(
              SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE,
              JSON.stringify(vehicleInfo),
            );

            if (needCreateQuote) {
              const vehicleInfoJSON = create(vehicleInfo);
              createQuote(
                productType,
                planPicked?.productKey,
                planPicked?.planKey,
                vehicleInfoJSON,
              );
            } else {
              changeActiveStep(nextStep);
            }
          });
        }}
      >
        {formatMessage({ id: 'Next' })}
      </Button>
    </div>
  );
};

export default VehicleInfoForm;
